/**
 * =====================================================
 * GLOBAL DISCOUNT CONFIGURATION
 * =====================================================
 * 
 * This file contains the global discount settings that apply
 * automatically to all products across the entire catalog.
 * 
 * HOW TO USE:
 * -----------
 * 1. Set GLOBAL_DISCOUNT to one of the allowed values:
 *    - 0      → No discount (shows original price only)
 *    - 0.05   → 5% discount
 *    - 0.10   → 10% discount
 *    - 0.15   → 15% discount
 * 
 * 2. Save the file and rebuild/refresh the project
 * 
 * 3. The discount will automatically apply to:
 *    - All product pages (Edible Cups, Cutlery, etc.)
 *    - Category listing pages
 *    - Shopping cart
 *    - Checkout
 * 
 * FORMULA:
 * --------
 * final_price = original_price * (1 - GLOBAL_DISCOUNT)
 * 
 * DISPLAY BEHAVIOR:
 * -----------------
 * - When discount is active (0.05, 0.10, 0.15):
 *   Shows original price with strikethrough + discounted price
 * 
 * - When discount is inactive (0):
 *   Shows only the original price
 * 
 * =====================================================
 */

// Allowed discount values
const ALLOWED_DISCOUNTS = [0, 0.05, 0.10, 0.15];

/**
 * GLOBAL DISCOUNT VALUE
 * =====================
 * Change this value to activate/deactivate the discount
 * 
 * Valid values: 0, 0.05, 0.10, 0.15
 */
export const GLOBAL_DISCOUNT = 0; // Set to 0.05, 0.10, or 0.15 to activate

// Validate the discount value
if (!ALLOWED_DISCOUNTS.includes(GLOBAL_DISCOUNT)) {
  console.error(
    `Invalid GLOBAL_DISCOUNT value: ${GLOBAL_DISCOUNT}. ` +
    `Allowed values are: ${ALLOWED_DISCOUNTS.join(', ')}. ` +
    `Defaulting to 0 (no discount).`
  );
}

/**
 * Get the validated global discount value
 * Returns 0 if the configured value is invalid
 */
export const getGlobalDiscount = () => {
  return ALLOWED_DISCOUNTS.includes(GLOBAL_DISCOUNT) ? GLOBAL_DISCOUNT : 0;
};

/**
 * Check if global discount is currently active
 */
export const isDiscountActive = () => {
  return getGlobalDiscount() > 0;
};

/**
 * Get the discount percentage as a display string (e.g., "10%")
 */
export const getDiscountPercentage = () => {
  const discount = getGlobalDiscount();
  return discount > 0 ? `${Math.round(discount * 100)}%` : '';
};

/**
 * Calculate the discounted price
 * @param {number} originalPrice - The original price
 * @returns {number} - The discounted price (or original if no discount)
 */
export const calculateDiscountedPrice = (originalPrice) => {
  const discount = getGlobalDiscount();
  if (discount === 0 || originalPrice === 0) {
    return originalPrice;
  }
  return originalPrice * (1 - discount);
};

/**
 * Get both original and discounted prices
 * @param {number} originalPrice - The original price
 * @returns {object} - { originalPrice, discountedPrice, hasDiscount, discountPercentage }
 */
export const getPriceInfo = (originalPrice) => {
  const discount = getGlobalDiscount();
  const hasDiscount = discount > 0 && originalPrice > 0;
  
  return {
    originalPrice,
    discountedPrice: hasDiscount ? originalPrice * (1 - discount) : originalPrice,
    hasDiscount,
    discountPercentage: hasDiscount ? `${Math.round(discount * 100)}%` : '',
    savings: hasDiscount ? originalPrice * discount : 0
  };
};

export default {
  GLOBAL_DISCOUNT,
  ALLOWED_DISCOUNTS,
  getGlobalDiscount,
  isDiscountActive,
  getDiscountPercentage,
  calculateDiscountedPrice,
  getPriceInfo
};
